import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner, Image, Accordion, Button } from 'react-bootstrap';
import config from '../../Common/Configurations/APIConfig';
import apiService from '../../Common/Configurations/APIService';
import './HolidayPackageDetails.css';
import EnquiryModal from './EnquiryModal';
import { LandingContext } from '../Profile/LandingContext';


const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'long', year: 'numeric',
    });
};
const formatCurrency = (amount, currencyCode) => {
    const symbols = { 'INR': '₹', 'USD': '$', 'EUR': '€' };
    const symbol = symbols[currencyCode] || currencyCode;
    return `${symbol}${amount.toLocaleString('en-IN')}`;
};

const PackageHeader = ({ pkg }) => (
    <div className="package-header mb-4">
        <h1 className="fw-bold">{pkg.packageName}</h1>
        <div className="icon-text text-muted">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10m0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6" /></svg>
            <span>{pkg.destination?.destinationName}</span>
        </div>
    </div>
);
const HighlightsBar = ({ pkg }) => (
    <Card className="highlights-bar mb-4">
        <Card.Body>
            <Row className="text-center">
                <Col><div className="highlight-item"><strong>Duration</strong><span>{pkg.duration}</span></div></Col>
                <Col><div className="highlight-item"><strong>Tour Type</strong><span>{pkg.type}</span></div></Col>
                <Col><div className="highlight-item"><strong>Group Size</strong><span>{pkg.noOfPersons} Persons</span></div></Col>
                <Col><div className="highlight-item"><strong>Price</strong><span>{pkg.currency} {pkg.price}/ person</span></div></Col>
            </Row>
        </Card.Body>
    </Card>
);
const ItineraryAccordion = ({ itineraries }) => {
    const sortedItineraries = [...itineraries].sort((a, b) => a.title.localeCompare(b.title, undefined, { numeric: true }));
    return (
        <div className="details-section">
            <h3 className="section-title">Itinerary</h3>
            <Accordion defaultActiveKey="0" alwaysOpen>
                {sortedItineraries.map((item, index) => (
                    <Accordion.Item eventKey={String(index)} key={item.packageItineraryId} className="mb-3">
                        <Accordion.Header>{item.title}: {item.location}</Accordion.Header>
                        <Accordion.Body>
                            <p>{item.description}</p><hr /><p className="mb-1"><strong>Meals:</strong> {item.meals}</p>{item.note && <p className="mb-0"><strong>Note:</strong> {item.note}</p>}
                        </Accordion.Body>
                    </Accordion.Item>
                ))}
            </Accordion>
        </div>
    );
};

const SightseeingSection = ({ sightSeeings }) => (
    <div className="details-section">
        <h3 className="section-title">Sightseeing</h3>
        {sightSeeings.map(sight => (
            <Card className="details-card mb-3" key={sight.packageSightSeeingId}>
                <Card.Body>
                    <div className="d-flex align-items-start">
                        <div className="flex-shrink-0 me-3">
                            <i class="ri-eye-2-line"></i>
                        </div>
                        <div className="flex-grow-1">
                            <Card.Title className="details-card-title">{sight.sightseeingName}</Card.Title>
                            <Card.Subtitle className="mb-2 text-muted">{sight.destination}</Card.Subtitle>
                            <Card.Text className="details-card-text">{sight.description}</Card.Text>
                            <Card.Text className="details-card-text text-muted small">Date: {formatDate(sight.date)}</Card.Text>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        ))}
    </div>
);
const TransfersSection = ({ transfers }) => (
    <div className="details-section">
        <h3 className="section-title">Transfers</h3>
        {transfers.map(transfer => (
            <Card className="details-card mb-3" key={transfer.packageTransferId}>
                <Card.Body>
                    <div className="d-flex align-items-start">
                        <div className="flex-shrink-0 me-3">
                            <i class="ri-car-line fs-5"></i>
                        </div>
                        <div className="flex-grow-1">
                            <Card.Title className="details-card-title">{transfer.fromLocation} to {transfer.toLocation}</Card.Title>
                            <Card.Text className="details-card-text"><strong>Date:</strong> {formatDate(transfer.date)} | <strong>Time:</strong> {transfer.pickupTime} | <strong>Vehicle:</strong> {transfer.vehicleDetails}</Card.Text>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        ))}
    </div>
);


// --- NEW: Refactored Components for better styling ---
const HotelDetailsSection = ({ hotelBookings }) => (
    <div className="details-section">
        <h3 className="section-title">Hotel Details</h3>
        {hotelBookings.map(hotel => (
            <Card className="details-card" key={hotel.packageHotelBookingId}>
                <Card.Body>
                    <div className="d-flex align-items-start">
                        <div className="flex-shrink-0 me-3">
                            <i class="ri-building-4-line fs-5"></i>
                        </div>
                        <div className="flex-grow-1">
                            <Card.Title className="details-card-title">{hotel.hotelName}, {hotel.city}</Card.Title>
                            <Card.Text className="details-card-text">
                                <strong>Check-in:</strong> {formatDate(hotel.checkInDate)} | <strong>Check-out:</strong> {formatDate(hotel.checkOutDate)} ({hotel.nights} Nights)
                            </Card.Text>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        ))}
    </div>
);

const FlightDetailsSection = ({ flightBookings }) => (
    <div className="details-section">
        <h3 className="section-title">Flight Details</h3>
        {flightBookings.map(flight => (
            <Card className="details-card" key={flight.packageFlightBookingId}>
                <Card.Body>
                    <div className="d-flex align-items-start">
                        <div className="flex-shrink-0 me-3">
                            <i class="ri-plane-line fs-5"></i>
                        </div>
                        <div className="flex-grow-1">
                    <Card.Title className="details-card-title">{flight.airline} ({flight.flightNumber})</Card.Title>
                    <Card.Text className="details-card-text"><strong>From:</strong> {flight.departureCity} | <strong>To:</strong> {flight.arrivalCity}</Card.Text>
                    <Card.Text className="details-card-text"><strong>Departure:</strong> {formatDate(flight.departureDateTime)} | <strong>Arrival:</strong> {formatDate(flight.arrivalDateTime)}</Card.Text>
                    </div>
                    </div>
                </Card.Body>
            </Card>
        ))}
    </div>
);

const InclusionsExclusionsSection = ({ includes, excludes }) => (
    <div className="details-section">
        <Row>
            <Col md={6}>
                <h4 className="sub-section-title">What's Included</h4>
                <ul className="includes-list">
                    {includes.map(item => <li key={item.packageIncludeId}>{item.description}</li>)}
                </ul>
            </Col>
            <Col md={6}>
                <h4 className="sub-section-title">What's Not Included</h4>
                <ul className="excludes-list">
                    {excludes.map(item => <li key={item.packageExcludeId}>{item.description}</li>)}
                </ul>
            </Col>
        </Row>
    </div>
);

const TermsAndConditionsSection = ({ termsandConditions }) => (
    <div className="details-section">
        <h3 className="section-title">Terms & Conditions</h3>
        {termsandConditions.map(term => (
            <div key={term.packageTermsAndConditionId} className='terms-item'>
                <strong>{term.title}</strong>
                <p className='text-muted small'>{term.description}</p>
            </div>
        ))}
    </div>
);

// --- Content Renderer Component ---
// This component renders the actual details once they are loaded.
const PackageDetailsContent = ({ details, onEnquiryClick }) => {
    // Destructure data inside this component
    const { pkg, hotelBookings, flightBookings, itineraries, transfers, sightSeeings, termsandConditions, includes, excludes } = { pkg: details.package, ...details };

    return (
        <div className="package-details-page">
            <Container>
                <Row>
                    <Col lg={12}>
                        <PackageHeader pkg={pkg} />
                        <HighlightsBar pkg={pkg} />
                        <Image src={pkg.imageSrc} alt={pkg.packageName} fluid rounded className="main-package-image mb-4" />
                        <Card className="enquiry-card-section my-4">
                            <Card.Body className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                                <div>
                                    <Card.Title className="mb-1 fw-bold">Have a question about this package?</Card.Title>
                                    <Card.Text className="text-muted mb-0">Our experts are here to help. Click to send us an enquiry.</Card.Text>
                                </div>
                                <Button variant="primary" size="lg" onClick={onEnquiryClick}>Send Enquiry</Button>
                            </Card.Body>
                        </Card>
                        <div className="details-section"><h3 className="section-title">About this tour</h3><p>{pkg.description}</p></div>
                        {itineraries?.length > 0 && <ItineraryAccordion itineraries={itineraries} />}
                        {sightSeeings?.length > 0 && <SightseeingSection sightSeeings={sightSeeings} />}
                        {transfers?.length > 0 && <TransfersSection transfers={transfers} />}
                        {hotelBookings?.length > 0 && <HotelDetailsSection hotelBookings={hotelBookings} />}
                        {flightBookings?.length > 0 && <FlightDetailsSection flightBookings={flightBookings} />}
                        {(includes?.length > 0 || excludes?.length > 0) && <InclusionsExclusionsSection includes={includes} excludes={excludes} />}
                        {termsandConditions?.length > 0 && <TermsAndConditionsSection termsandConditions={termsandConditions} />}
                    </Col>
                </Row>
            </Container>
        </div>
    );
};


// --- Main Package Details Component ---
const HolidayPackageDetails = () => {
    const { packageId } = useParams();
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showEnquiryModal, setShowEnquiryModal] = useState(false);
    const [hasModalOpenedAutomatically, setHasModalOpenedAutomatically] = useState(false);

    const handleShowEnquiryModal = () => setShowEnquiryModal(true);
    const handleCloseEnquiryModal = () => setShowEnquiryModal(false);

    useEffect(() => {
        const getPackageDetails = async () => {
            if (!packageId) return;
            setLoading(true);
            try {
                const response = await apiService.get(`${config.GETPACKAGEDETAILS}/${packageId}`);
                setDetails(response);
            } catch (err) {
                console.error("Failed to fetch package details:", err);
                setError("Could not load package details. Please try again later.");
            } finally {
                setLoading(false);
            }
        };
        getPackageDetails();
    }, [packageId]);

    useEffect(() => {
        if (details && !loading && !hasModalOpenedAutomatically) {
            const timer = setTimeout(() => {
                setShowEnquiryModal(true);
                setHasModalOpenedAutomatically(true);
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [details, loading, hasModalOpenedAutomatically]);
    
    // MODIFIED: The component now always returns the layout.
    // Conditional rendering happens inside the `page-content`.
    return (
        <>
            <div className="main-content">
                <div className="page-content" style={{ paddingTop: '2rem' }}>
                    {loading ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                            <Spinner animation="border" variant="primary" />
                            <span className="ms-3 fs-5">Loading Package...</span>
                        </div>
                    ) : error ? (
                        <Container>
                             <div className="text-center py-5">
                                <h2 className="text-danger">{error}</h2>
                             </div>
                        </Container>
                    ) : !details || !details.package ? (
                        <Container>
                            <div className="text-center py-5">
                                <h2>Package not found.</h2>
                            </div>
                        </Container>
                    ) : (
                        <PackageDetailsContent details={details} onEnquiryClick={handleShowEnquiryModal} />
                    )}
                </div>
            </div>
            {details && (
                <EnquiryModal show={showEnquiryModal} handleClose={handleCloseEnquiryModal} packageDetails={details} />
            )}
        </>
    );
};

export default HolidayPackageDetails;